'use client';

import { useParams } from 'next/navigation';
import clsx from 'clsx';
import { useState, useRef } from 'react';
import { useScroll, useMotionValueEvent } from 'framer-motion';
import { Breakpoint } from '@/features/breakpoint';
import { useBreakpoint } from '@/features/breakpoint/contexts';
import { usePathname } from '@/navigation';
import { useLayoutSegmentsMatched } from '@/hooks/use-layout-segments-matched';

import { HeaderEvent } from '@/components/layout/header/header-event';
import { TopMenu } from '@/features/top-menu';
import { MenuBar } from '@/components/layout/menu-bar';
import { isNumberGames, isVirtualSports } from '@/services/settings.service';
import { ThirdPartyTopMenu } from '@/features/top-menu/third-party-top-menu';
import { useAppearanceStore } from '@/store/appearance';
import { ViewEnum } from '@/types';

export const HeaderMenuSection = ({
	children
}: {
	children: React.ReactNode
}) => {
	const { matchId, sport } = useParams();
	const pathname = usePathname();
	const isInPhSection = pathname.startsWith('/section/ph');
	const isSingleMatchPage = matchId !== undefined;
	const isNumberGame = isNumberGames(Number(sport));
	const isVirtualSport = isVirtualSports(Number(sport));
	const showHeaderEvent = !useLayoutSegmentsMatched(['bets', 'promotion']) && !isSingleMatchPage;
	const showMenuBar = 
		!useLayoutSegmentsMatched([
			'favorites', 
			'search', 
			'bets', 
			'lobby', 
			'score', 
			'jackpot', 
			'widget', 
			'promotion', 
			'streamer',
			'golden-duel'
		]) && 
		!isSingleMatchPage &&
		!isInPhSection;
	const view = useAppearanceStore(state => state.view);
	const { breakpoint } = useBreakpoint();

	// Galaxy 手機版滾動超過一定距離時才顯示/隱藏 top-nav & sports-menu
	const [visible, setVisible] = useState(true);
	const [hasScrolledDown, setHasScrolledDown] = useState(false);
	const { scrollY } = useScroll();
	const previousScrollY = useRef(0);

	useMotionValueEvent(scrollY, 'change', (latest) => {
		if (view === ViewEnum.galaxy) {
			const isScrollingDown = previousScrollY.current < latest;
			const scrollDistance = Math.abs(latest - previousScrollY.current);

			// 檢查是否已經往下滾動超過指定距離
			if (latest > 50) {
				setHasScrolledDown(true);
			}

			// 只有在滾動超過一定距離時才觸發隱藏/顯示
			if (scrollDistance > 10 && breakpoint <= Breakpoint.md) {
				if (hasScrolledDown) {
					setVisible(!isScrollingDown);
				}
			}

			previousScrollY.current = latest;
		}
	});
	
	return (
		<>
			<HeaderEvent
				className={clsx('header-event-top', {
					'm-display-none': !showHeaderEvent
				})}
			/>
			{children}
			{view === ViewEnum.galaxy && (isNumberGame || isVirtualSport) ? null : <TopMenu visible={visible} />}
			{showMenuBar ? <MenuBar visible={visible} /> : null}
		</>
	);
};